import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { CoArchitectLogo, AlertCircle, GoogleIcon } from '../components/Icons';
import { API } from '../contexts/AuthContext';

const GOOGLE_AUTH_URL = `${API}/auth/google`;

function Login() {
  const navigate               = useNavigate();
  const location               = useLocation();
  const { login, bypassLogin } = useAuth();

  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [showPass, setShowPass] = useState(false);

  // Surface any OAuth error passed back from the callback
  const oauthError = new URLSearchParams(location.search).get('error');

  const canSubmit = email.includes('@') && password.length >= 8;

  const from = (location.state as { from?: string } | null)?.from || '/home';

  const handleEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password);
      navigate(from, { replace: true });
    } catch (err: any) {
      const msg: string = err.response?.data?.error ?? '';
      setError(msg || 'Invalid email or password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div style={{ width: '100%', maxWidth: 420 }}>
        <button className="auth-back-btn" onClick={() => navigate('/')}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
          Back to Home
        </button>
      </div>

      <div className="auth-card">
        {/* Brand */}
        <div className="auth-brand">
          <div className="auth-brand-logo">
            <CoArchitectLogo size={80} />
          </div>
          <h1>Co-Architect</h1>
          <p>AI-powered interior design previews</p>
        </div>

        {/* OAuth error banner */}
        {oauthError && (
          <div className="alert alert-error" role="alert" style={{ marginBottom: 12 }}>
            <AlertCircle size={15} style={{ flexShrink: 0 }} />
            <span>
              {oauthError === 'google_failed'
                ? 'Google sign-in failed. Please try again.'
                : 'Sign-in failed. Please try again.'}
            </span>
          </div>
        )}

        <a
          href={GOOGLE_AUTH_URL}
          className="btn-google"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                   height: 46, width: '100%', textDecoration: 'none', marginBottom: 4 }}
        >
          <GoogleIcon size={18} />
          Continue with Google
        </a>

        <div className="auth-divider"><span>or sign in with email</span></div>

        {/* Email / password */}
        <form onSubmit={handleEmail} className="auth-form" noValidate>
          <div className="form-field">
            <label className="form-label" htmlFor="email">Email</label>
            <input
              id="email"
              className="form-input"
              type="email"
              autoComplete="email"
              placeholder="your@email.com"
              value={email}
              onChange={e => { setEmail(e.target.value); if (error) setError(''); }}
              required
            />
          </div>

          <div className="form-field">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <label className="form-label" htmlFor="password">Password</label>
              <Link to="/forgot-password" className="auth-link" style={{ fontSize: '0.8125rem' }}>Forgot password?</Link>
            </div>
            <div style={{ position: 'relative' }}>
              <input
                id="password"
                className="form-input"
                type={showPass ? 'text' : 'password'}
                autoComplete="current-password"
                placeholder="Min. 8 characters"
                value={password}
                onChange={e => { setPassword(e.target.value); if (error) setError(''); }}
                style={{ paddingRight: 40 }}
                required
              />
              <button
                type="button"
                onClick={() => setShowPass(v => !v)}
                style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                         background: 'none', border: 'none', cursor: 'pointer',
                         color: 'var(--text-faint)', fontSize: 12, padding: 4 }}
                aria-label={showPass ? 'Hide password' : 'Show password'}
              >
                {showPass ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>

          {error && (
            <span className="form-error" role="alert">
              <AlertCircle size={13} />{error}
            </span>
          )}

          <button type="submit" className="btn btn-primary" disabled={loading || !canSubmit}>
            {loading
              ? <><span className="spinner" style={{ width: 18, height: 18, borderWidth: 2 }} /> Signing in…</>
              : 'Sign In'}
          </button>
        </form>

        <div className="auth-divider"><span>New to Co-Architect?</span></div>

        <div className="auth-footer">
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/signup')}>
            Create Account
          </button>
        </div>

        {/* Dev bypass — only visible outside production */}
        {process.env.NODE_ENV !== 'production' && (
          <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px dashed var(--border)', textAlign: 'center' }}>
            <button
              type="button"
              className="btn btn-ghost"
              style={{ fontSize: '0.75rem', color: 'var(--text-faint)' }}
              onClick={() => { bypassLogin(); navigate(from, { replace: true }); }}
            >
              ⚙ Dev bypass (9892741471)
            </button>
          </div>
        )}
      </div>

      {/* Social + legal footer */}
      <div className="auth-social-footer">
        <a href="https://www.instagram.com/coarchitect.app" target="_blank" rel="noopener noreferrer" className="auth-social-row">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="2" width="20" height="20" rx="5"/>
            <circle cx="12" cy="12" r="4"/>
            <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none"/>
          </svg>
          @coarchitect.app
        </a>
        <div className="auth-legal-row">
          <Link to="/privacy" className="auth-legal-link">Privacy Policy</Link>
          <span className="auth-legal-sep" />
          <Link to="/terms" className="auth-legal-link">Terms of Service</Link>
        </div>
      </div>
    </div>
  );
}

export default Login;
